import React, { useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { FaTimes as X, FaTrash as Trash } from 'react-icons/fa'
import {
  useProjectCollaborators,
  useShareProject,
  useUnshareProject,
  useRevokeProjectInvite,
  type ProjectCollaborator,
} from '@/hooks/useSequencerSharing'
import { APIError } from '@/lib/api'

interface ShareProjectDialogProps {
  isOpen: boolean
  onClose: () => void
  projectId: string | null
  projectName: string
}

export const ShareProjectDialog: React.FC<ShareProjectDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
}) => {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<'viewer' | 'editor'>('editor')
  const [expiresAt, setExpiresAt] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const { data: collaborators = [], isLoading } = useProjectCollaborators(
    projectId,
    isOpen,
  )
  const share = useShareProject(projectId)
  const unshare = useUnshareProject(projectId)
  const revoke = useRevokeProjectInvite(projectId)

  const handleShare = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    const submittedEmail = email.trim()
    try {
      const result = await share.mutateAsync({
        email: submittedEmail,
        role,
        expiresAt: expiresAt
          ? new Date(expiresAt + 'T23:59:59Z').toISOString()
          : undefined,
      })
      const displayEmail = result.recipientEmail || submittedEmail
      if (result.status === 'active') {
        setNotice(`Shared with ${displayEmail}.`)
      } else {
        setNotice(
          `Invitation sent to ${displayEmail}. They'll get access automatically when they sign up.`,
        )
      }
      setEmail('')
      setExpiresAt('')
    } catch (err) {
      setError(
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to share pattern',
      )
    }
  }

  const handleRemove = async (c: ProjectCollaborator) => {
    setError(null)
    setNotice(null)
    try {
      if (c.status === 'active') {
        await unshare.mutateAsync(c.recipientUserId)
      } else {
        await revoke.mutateAsync(c.recipientEmail)
      }
    } catch (err) {
      setError(
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to revoke share',
      )
    }
  }

  const handleChangeRole = async (
    c: ProjectCollaborator,
    newRole: 'viewer' | 'editor',
  ) => {
    if (c.role === newRole) return
    setError(null)
    setNotice(null)
    try {
      await share.mutateAsync({
        email: c.recipientEmail,
        role: newRole,
        expiresAt: c.expiresAt,
      })
      setNotice(`${c.recipientEmail} is now ${newRole}.`)
    } catch (err) {
      setError(
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to change role',
      )
    }
  }

  const removePending = revoke.isPending || unshare.isPending
  const rolePending = share.isPending

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          setEmail('')
          setExpiresAt('')
          setError(null)
          setNotice(null)
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md max-h-[90vh] bg-surface-raised rounded-xl shadow-high z-50 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-brd-subtle">
            <div>
              <Dialog.Title className="text-lg font-semibold text-content-primary">
                Share pattern
              </Dialog.Title>
              <Dialog.Description className="text-xs text-content-tertiary mt-0.5 truncate">
                {projectName}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="text-content-secondary hover:text-content-primary transition-colors duration-150">
                <X size={20} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            <form onSubmit={handleShare} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-raised text-content-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
                  placeholder="teammate@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">
                  Role
                </label>
                <div className="flex gap-2">
                  {(['viewer', 'editor'] as const).map(r => (
                    <button
                      type="button"
                      key={r}
                      onClick={() => setRole(r)}
                      className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors duration-150 ${
                        role === r
                          ? 'border-accent bg-accent-muted text-accent'
                          : 'border-brd-subtle text-content-secondary hover:bg-accent-muted'
                      }`}
                    >
                      {r === 'viewer'
                        ? 'Viewer (read-only)'
                        : 'Editor (can save)'}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-content-secondary mb-1">
                  Expires (optional)
                </label>
                <input
                  type="date"
                  value={expiresAt}
                  onChange={e => setExpiresAt(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 rounded-lg border border-brd-subtle bg-surface-raised text-content-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
                />
                <p className="text-[10px] text-content-tertiary mt-0.5">
                  Leave blank for permanent access. Useful for time-bound jam
                  sessions.
                </p>
              </div>
              {error && (
                <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
                  {error}
                </div>
              )}
              {notice && !error && (
                <div className="text-xs text-success bg-success/5 border border-success/20 rounded-md px-3 py-2">
                  {notice}
                </div>
              )}
              <button
                type="submit"
                disabled={share.isPending || !email.trim()}
                className="w-full px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 font-medium"
              >
                {share.isPending ? 'Sharing…' : 'Share'}
              </button>
              <p className="text-[11px] text-content-tertiary">
                If the recipient doesn&apos;t have an account yet, we&apos;ll
                send them an invitation email. The share activates automatically
                when they sign up.
              </p>
            </form>

            <div className="pt-4 border-t border-brd-subtle">
              <div className="text-sm font-medium text-content-secondary mb-2">
                Current collaborators
              </div>
              {isLoading ? (
                <div className="text-xs text-content-tertiary">Loading…</div>
              ) : collaborators.length === 0 ? (
                <div className="text-xs text-content-tertiary italic">
                  No one else has access yet.
                </div>
              ) : (
                <ul className="space-y-1">
                  {collaborators.map(c => {
                    const key =
                      c.status === 'active'
                        ? `active-${c.recipientUserId}`
                        : `pending-${c.recipientEmail}`
                    return (
                      <li
                        key={key}
                        className="flex items-center justify-between px-3 py-2 rounded-lg bg-surface-sunken"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-content-primary truncate flex items-center gap-2">
                            <span className="truncate">{c.recipientEmail}</span>
                            {c.status === 'pending' && (
                              <span
                                className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[color:color-mix(in_srgb,var(--warning)_20%,transparent)] text-warning uppercase tracking-wider"
                                title="Invitation sent. Share activates when they sign up."
                              >
                                Pending
                              </span>
                            )}
                          </div>
                          {c.expiresAt && (
                            <div className="text-[11px] text-content-tertiary">
                              Expires{' '}
                              {new Date(c.expiresAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <select
                          value={c.role}
                          onChange={e =>
                            handleChangeRole(
                              c,
                              e.target.value as 'viewer' | 'editor',
                            )
                          }
                          disabled={rolePending}
                          className="mr-1 text-[11px] bg-surface-raised border border-brd-subtle rounded px-1.5 py-1 text-content-primary focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-halo)] disabled:opacity-50"
                          aria-label={`Change role for ${c.recipientEmail}`}
                          title="Change role"
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                        </select>
                        <button
                          onClick={() => handleRemove(c)}
                          disabled={removePending}
                          className="p-1.5 rounded text-content-tertiary hover:text-destructive hover:bg-destructive/5 transition-colors duration-150"
                          title={
                            c.status === 'active'
                              ? 'Revoke access'
                              : 'Cancel invitation'
                          }
                        >
                          <Trash size={12} />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
